import { lazy, Suspense, useState, type ReactNode } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { createQueryClient } from '@/api/queryClient';

/**
 * Devtools, dev builds only.
 *
 * `import.meta.env.DEV` is statically replaced at build time, so the whole
 * ternary folds to `null` in production and Rollup drops the dynamic import
 * with it — the devtools bundle is never emitted, let alone fetched.
 */
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(async () => ({
      default: (await import('@tanstack/react-query-devtools')).ReactQueryDevtools,
    }))
  : null;

/**
 * Server-state provider for the whole app.
 *
 * The client is created inside `useState` rather than at module scope so that
 * each mounted app (tests, future multi-root embeds) gets its own cache, and so
 * a Fast Refresh cycle does not silently share a cache across reloads.
 */
export function QueryProvider({ client, children }: { client?: QueryClient; children: ReactNode }) {
  const [fallbackClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={client ?? fallbackClient}>
      {children}
      {ReactQueryDevtools && (
        <Suspense fallback={null}>
          {/*
            bottom-right, not bottom-left: the sidebar footer owns the
            bottom-left corner (account switcher and the online indicator), and
            the devtools button sat directly on top of the status text.
          */}
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
